<template>
  <div class="create-date-container">
    <div class="header">
      <div class="header__title">{{ title }}</div>
    </div>

    <div class="form-container">
      <EventDateForm
        ref="refEventDateForm"
        :values="formValues"
        @onSubmitSuccess="handleSubmitForm"
      />
    </div>

    <div class="footer">
      <div class="footer__buttons">
        <BaseButton outline @click="() => $router.back()">
          {{ $t('BACK') }}
        </BaseButton>

        <BaseButton
          theme="blue"
          :data-cy="'submit-date'"
          :disabled="isSubmitting"
          @click="submitEventDateForm"
        >
          {{ submitButtonText }}
        </BaseButton>
      </div>
    </div>
  </div>
</template>

<script>
// TODO: Refactor to Vue3
import BaseButton from '@/components/generics/base/BaseButton.vue'
import EventDateForm from '@/components/product/product_components/component_events/date_form_components/EventDateForm.vue'
import { useDate } from '@/hooks/swr/useDate'
import {
  createEventDate,
  updateProductEvent,
} from '@/service/v3/endpoints/events'
import { useQueryClient } from '@tanstack/vue-query'
import { QUERY_KEY_EVENT } from '@/queries/events/eventKeys.js'
import { useRouter } from 'vue-router/composables'
import { ROUTE_EVENT_DATE } from '@/router/routes/routeNames.js'
import { useEventQuery } from '@/queries/events/eventQueries.js'

export default {
  name: 'EventDateFormView',
  components: {
    BaseButton,
    EventDateForm,
  },
  emits: ['onCancel', 'onSubmitSuccess'],
  setup() {
    const router = useRouter()
    const route = router.currentRoute
    const queryClient = useQueryClient()
    const courseId = Number(route.params.courseId)
    const productId = Number(route.params.productId)

    const { unwrappedDate } = useDate(courseId)
    const { data: event } = useEventQuery(productId)

    return {
      queryClient,
      productId,
      courseId,
      unwrappedDate,
      router,
      event,
    }
  },
  data() {
    return { isSubmitting: false }
  },
  computed: {
    title() {
      return this.isExistingDate
        ? this.$t('product_events.EDIT_DATE')
        : this.$t('product_events.ADD_DATE')
    },

    submitButtonText() {
      return this.isExistingDate
        ? this.$t('product_events.SAVE_CHANGES')
        : this.$t('product_events.ADD_DATE')
    },
    isExistingDate() {
      return this.courseId !== 0 && this.courseId !== undefined
    },
    formValues() {
      if (this.isExistingDate) {
        const {
          attendees_count_max,
          instructor,
          instructor_record,
          location,
          room,
          start_datetime,
          end_datetime,
          signup_deadline,
          course_material,
          meeting_link,
          waiting_list_enabled,
          admission_strategy,
          time_zone,
        } = this.unwrappedDate

        return {
          attendees_count_max,
          instructor,
          instructor_record: {
            id: instructor_record?.id,
          },
          location,
          room,
          start_datetime,
          end_datetime,
          signup_deadline,
          course_material,
          meeting_link,
          waiting_list_enabled,
          admission_strategy,
          time_zone,
        }
      }

      const latestCourse = this.event.courses.reduce((latestCourse, course) => {
        if (!latestCourse) return course

        return Number(course.id) > Number(latestCourse.id)
          ? course
          : latestCourse
      }, undefined)

      if (latestCourse) {
        // Copy values from the latest course as default values for new course, except for dates (we currently don't want to try to guess these)
        const {
          attendees_count_max,
          instructor,
          instructor_record,
          location,
          room,
          course_material,
          meeting_link,
          admission_strategy,
        } = latestCourse

        return {
          attendees_count_max,
          instructor,
          instructor_record: {
            id: instructor_record?.id,
          },
          location,
          room,
          course_material,
          meeting_link,
          admission_strategy,
        }
      }

      return undefined
    },
  },
  methods: {
    handleSubmitForm: async function (date) {
      this.isSubmitting = true
      if (this.isExistingDate) {
        await this.updateEventDate(date)
      } else {
        await this.createEventDate(date)
      }
      this.isSubmitting = false
    },
    submitEventDateForm() {
      this.$refs.refEventDateForm.submit()
    },
    // API
    async createEventDate(date) {
      try {
        const response = await createEventDate(this.productId, date)
        this.$toast(
          { text: this.$t('product_events.toast_messages.ADD_DATE_SUCCESS') },
        )

        await this.router.replace({
          name: ROUTE_EVENT_DATE,
          params: { productId: this.productId, courseId: response.data.id },
        })
        this.queryClient.invalidateQueries({
          queryKey: [QUERY_KEY_EVENT],
        })
      } catch (error) {
        this.$toast(
          { text: this.$t('product_events.toast_messages.ADD_DATE_ERROR') },
        )
      }
    },
    async updateEventDate(date, dateId) {
      const response = await updateProductEvent(
        this.productId,
        this.courseId,
        date
      )
      if (response.error) {
        this.$toast(
          { text: this.$t('product_events.toast_messages.UPDATE_DATE_ERROR') },
        )
      } else {
        this.$emit('onSubmitSuccess', this.courseId)
        this.$toast(
          {
            text: this.$t('product_events.toast_messages.UPDATE_DATE_SUCCESS'),
          },
        )
        await this.router.replace({
          name: ROUTE_EVENT_DATE,
          params: { productId: this.productId, courseId: this.courseId },
        })

        this.queryClient.invalidateQueries({
          queryKey: [QUERY_KEY_EVENT],
        })
      }
    },
  },
}
</script>

<style lang="scss" scoped>
.create-date-container {
  display: flex;
  flex: 1 1 0;
  flex-direction: column;
  align-self: stretch;

  .header {
    margin-bottom: $spacing-3xs;

    &__title {
      @include subtitle;
      font-size: $font-size-xl;
    }
  }

  .form-container {
    overflow-y: auto;
    flex: 1 1 0;
  }

  .footer {
    display: flex;
    align-items: center;
    gap: $spacing-s;
    border-top: $border-default;
    padding-top: $spacing-4xs;
    flex-wrap: wrap;
    justify-content: end;

    &__buttons {
      display: flex;
      gap: $spacing-3xs;

      .attensi-button {
        margin: 0 !important;
      }
    }
  }
}

.modal-cancel-date-container {
  text-align: center;
  padding: $spacing-l;

  .title {
    @include subtitle;
    margin: $spacing-m 0;
  }
}
</style>
